import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import MediaLibraryPickerModal from './MediaLibraryPickerModal';

const makeId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const deepClone = (value) => JSON.parse(JSON.stringify(value));

const createTextItem = () => ({
  id: makeId(),
  type: 'text',
  content: 'New text',
  styles: {
    color: '#111111',
    font_size: 18,
    font_weight: 400,
    font_style: 'normal',
    line_height: 1.5,
  },
});

const createImageItem = () => ({
  id: makeId(),
  type: 'image',
  src: '',
  alt: 'Image',
  styles: {
    width: 100,
    fit: 'cover',
    border_radius: 0,
  },
});

const createButtonItem = () => ({
  id: makeId(),
  type: 'button',
  text: 'Click here',
  url: '#',
  styles: {
    variant: 'solid',
    background_color: '#1e3a8a',
    text_color: '#ffffff',
    font_size: 16,
    font_weight: 600,
    border_radius: 10,
    padding_y: 12,
    padding_x: 20,
  },
});

const createColumn = () => ({
  id: makeId(),
  width: 12,
  align: 'left',
  items: [createTextItem()],
});

const createSection = () => ({
  id: makeId(),
  name: 'New section',
  padding: { top: 70, bottom: 70 },
  gap: 24,
  background: {
    type: 'color',
    value: '#ffffff',
    overlay: '',
  },
  columns: [createColumn()],
});

const fallbackLayout = {
  settings: {
    page_title: 'Kingdom Vision International Network',
    background_color: '#f4f4ef',
    text_color: '#1f1f1f',
    max_width: 1200,
  },
  sections: [],
};

const itemByType = (type) => {
  if (type === 'image') return createImageItem();
  if (type === 'button') return createButtonItem();
  return createTextItem();
};

const formatApiError = (error, fallback) => {
  const payload = error?.response?.data;
  if (payload?.errors && typeof payload.errors === 'object') {
    const messages = Object.values(payload.errors).find((entry) => Array.isArray(entry) && entry.length > 0);
    if (messages) {
      return messages.join(' ');
    }
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  return fallback;
};

const HomepageBuilder = () => {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [pickerTarget, setPickerTarget] = useState(null);

  const loadLayout = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotice('');

    try {
      const { data } = await axios.get('/admin/homepage');
      setLayout(data.layout || fallbackLayout);
      setUpdatedAt(data.updated_at || '');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to load homepage layout.'));
      setLayout(fallbackLayout);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  const publicHomeUrl = useMemo(() => {
    const base = process.env.REACT_APP_PUBLIC_SITE_URL;
    if (base) {
      return base.replace(/\/$/, '');
    }

    if (typeof window === 'undefined') {
      return '/';
    }

    return window.location.origin;
  }, []);

  const mutateLayout = (mutator) => {
    setLayout((previous) => {
      if (!previous) return previous;
      const next = deepClone(previous);
      mutator(next);
      return next;
    });
  };

  const uploadImage = async (file, handleUrl, key) => {
    if (!file) return;
    setUploadingKey(key);
    setError('');
    setNotice('');

    const formData = new FormData();
    formData.append('image', file);

    try {
      const { data } = await axios.post('/admin/homepage/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleUrl(data.url || '');
      setNotice('Image uploaded.');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Image upload failed.'));
    } finally {
      setUploadingKey('');
    }
  };

  const handleMediaLibrarySelect = (url) => {
    if (!url || !pickerTarget) return;

    mutateLayout((next) => {
      if (pickerTarget.type === 'section-background') {
        const section = next.sections?.[pickerTarget.sectionIndex];
        if (section?.background) {
          section.background.value = url;
        }
      }

      if (pickerTarget.type === 'item-image') {
        const item =
          next.sections?.[pickerTarget.sectionIndex]?.columns?.[pickerTarget.columnIndex]?.items?.[
            pickerTarget.itemIndex
          ];
        if (item) {
          item.src = url;
        }
      }
    });

    setPickerTarget(null);
    setError('');
    setNotice('Image selected from media library.');
  };

  const handleSave = async () => {
    if (!layout) return;
    setSaving(true);
    setError('');
    setNotice('');

    try {
      const { data } = await axios.put('/admin/homepage', { layout });
      setLayout(data.layout || layout);
      setUpdatedAt(data.updated_at || '');
      setNotice(data.message || 'Homepage updated.');
    } catch (requestError) {
      setError(formatApiError(requestError, 'Failed to save homepage layout.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="card">Loading homepage editor...</div>;
  }

  if (!layout) {
    return <div className="card">Homepage layout unavailable.</div>;
  }

  return (
    <section className="homepage-builder">
      <div className="card">
        <div className="homepage-builder__header">
          <div>
            <h2>Homepage Builder</h2>
            <p className="text-muted">
              Build page sections, columns, and items. Edit text, images, and buttons with style controls.
            </p>
            {updatedAt && (
              <p className="text-muted" style={{ marginTop: '0.4rem' }}>
                Last saved: {new Date(updatedAt).toLocaleString()}
              </p>
            )}
          </div>

          <div className="homepage-builder__actions">
            <a className="btn btn-muted" href={`${publicHomeUrl}/`} target="_blank" rel="noreferrer">
              View Homepage
            </a>
            <Link className="btn btn-muted" to="/admin/media-library">
              Media Library
            </Link>
            <button className="btn btn-muted" type="button" onClick={loadLayout}>
              Reload
            </button>
            <button className="btn btn-primary" type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Homepage'}
            </button>
          </div>
        </div>

        {notice && <div className="notice notice-success">{notice}</div>}
        {error && <div className="notice notice-danger">{error}</div>}
      </div>

      <div className="card">
        <h3>Page Settings</h3>
        <div className="homepage-builder__grid">
          <div className="field">
            <label htmlFor="page-title">Page title</label>
            <input
              id="page-title"
              className="input"
              value={layout.settings?.page_title || ''}
              onChange={(event) =>
                mutateLayout((next) => {
                  next.settings.page_title = event.target.value;
                })
              }
            />
          </div>

          <div className="field">
            <label htmlFor="max-width">Max width (px)</label>
            <input
              id="max-width"
              className="input"
              type="number"
              min={760}
              max={1680}
              value={layout.settings?.max_width ?? 1200}
              onChange={(event) =>
                mutateLayout((next) => {
                  next.settings.max_width = Number(event.target.value);
                })
              }
            />
          </div>

          <div className="field">
            <label htmlFor="bg-color">Page background</label>
            <input
              id="bg-color"
              className="input"
              type="color"
              value={layout.settings?.background_color || '#f4f4ef'}
              onChange={(event) =>
                mutateLayout((next) => {
                  next.settings.background_color = event.target.value;
                })
              }
            />
          </div>

          <div className="field">
            <label htmlFor="text-color">Default text color</label>
            <input
              id="text-color"
              className="input"
              type="color"
              value={layout.settings?.text_color || '#1f1f1f'}
              onChange={(event) =>
                mutateLayout((next) => {
                  next.settings.text_color = event.target.value;
                })
              }
            />
          </div>
        </div>
      </div>

      <div className="homepage-builder__sections">
        {layout.sections?.map((section, sectionIndex) => (
          <article key={section.id || sectionIndex} className="card homepage-section">
            <div className="homepage-builder__row">
              <h3>Section {sectionIndex + 1}</h3>
              <button
                className="btn btn-danger"
                type="button"
                onClick={() =>
                  mutateLayout((next) => {
                    next.sections.splice(sectionIndex, 1);
                  })
                }
              >
                Remove Section
              </button>
            </div>

            <div className="homepage-builder__grid">
              <div className="field">
                <label>Section Name</label>
                <input
                  className="input"
                  value={section.name || ''}
                  onChange={(event) =>
                    mutateLayout((next) => {
                      next.sections[sectionIndex].name = event.target.value;
                    })
                  }
                />
              </div>

              <div className="field">
                <label>Top padding (px)</label>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={260}
                  value={section.padding?.top ?? 70}
                  onChange={(event) =>
                    mutateLayout((next) => {
                      next.sections[sectionIndex].padding.top = Number(event.target.value);
                    })
                  }
                />
              </div>

              <div className="field">
                <label>Bottom padding (px)</label>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={260}
                  value={section.padding?.bottom ?? 70}
                  onChange={(event) =>
                    mutateLayout((next) => {
                      next.sections[sectionIndex].padding.bottom = Number(event.target.value);
                    })
                  }
                />
              </div>

              <div className="field">
                <label>Column gap (px)</label>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={80}
                  value={section.gap ?? 24}
                  onChange={(event) =>
                    mutateLayout((next) => {
                      next.sections[sectionIndex].gap = Number(event.target.value);
                    })
                  }
                />
              </div>

              <div className="field">
                <label>Background Type</label>
                <select
                  className="input"
                  value={section.background?.type || 'color'}
                  onChange={(event) =>
                    mutateLayout((next) => {
                      next.sections[sectionIndex].background.type = event.target.value;
                    })
                  }
                >
                  <option value="color">Color</option>
                  <option value="image">Image</option>
                </select>
              </div>

              {section.background?.type === 'color' ? (
                <div className="field">
                  <label>Background Color</label>
                  <input
                    className="input"
                    type="color"
                    value={section.background?.value || '#ffffff'}
                    onChange={(event) =>
                      mutateLayout((next) => {
                        next.sections[sectionIndex].background.value = event.target.value;
                      })
                    }
                  />
                </div>
              ) : (
                <div className="field">
                  <label>Background Image URL</label>
                  <input
                    className="input"
                    value={section.background?.value || ''}
                    onChange={(event) =>
                      mutateLayout((next) => {
                        next.sections[sectionIndex].background.value = event.target.value;
                      })
                    }
                  />
                  <button
                    className="btn btn-muted"
                    type="button"
                    style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}
                    onClick={() =>
                      setPickerTarget({
                        type: 'section-background',
                        sectionIndex,
                      })
                    }
                  >
                    Choose from Media Library
                  </button>
                </div>
              )}

              {section.background?.type === 'image' && (
                <>
                  <div className="field">
                    <label>Upload background image</label>
                    <input
                      className="input"
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        uploadImage(
                          file,
                          (url) =>
                            mutateLayout((next) => {
                              next.sections[sectionIndex].background.value = url;
                            }),
                          `section-${sectionIndex}`
                        );
                        event.target.value = '';
                      }}
                    />
                    {uploadingKey === `section-${sectionIndex}` && (
                      <small className="text-muted">Uploading...</small>
                    )}
                  </div>

                  <div className="field">
                    <label>Overlay (css color)</label>
                    <input
                      className="input"
                      value={section.background?.overlay || ''}
                      onChange={(event) =>
                        mutateLayout((next) => {
                          next.sections[sectionIndex].background.overlay = event.target.value;
                        })
                      }
                      placeholder="rgba(0,0,0,0.45)"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="homepage-builder__columns">
              {section.columns?.map((column, columnIndex) => (
                <div key={column.id || columnIndex} className="homepage-column">
                  <div className="homepage-builder__row">
                    <h4>Column {columnIndex + 1}</h4>
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={() =>
                        mutateLayout((next) => {
                          next.sections[sectionIndex].columns.splice(columnIndex, 1);
                        })
                      }
                    >
                      Remove Column
                    </button>
                  </div>

                  <div className="homepage-builder__grid">
                    <div className="field">
                      <label>Width (1-12)</label>
                      <input
                        className="input"
                        type="number"
                        min={1}
                        max={12}
                        value={column.width ?? 12}
                        onChange={(event) =>
                          mutateLayout((next) => {
                            next.sections[sectionIndex].columns[columnIndex].width = Number(event.target.value);
                          })
                        }
                      />
                    </div>

                    <div className="field">
                      <label>Align</label>
                      <select
                        className="input"
                        value={column.align || 'left'}
                        onChange={(event) =>
                          mutateLayout((next) => {
                            next.sections[sectionIndex].columns[columnIndex].align = event.target.value;
                          })
                        }
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                  </div>

                  <div className="homepage-items">
                    {column.items?.map((item, itemIndex) => (
                      <div key={item.id || itemIndex} className="homepage-item">
                        <div className="homepage-builder__row">
                          <h5>Item {itemIndex + 1}</h5>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <select
                              className="input"
                              value={item.type || 'text'}
                              onChange={(event) =>
                                mutateLayout((next) => {
                                  const replacement = itemByType(event.target.value);
                                  replacement.id = next.sections[sectionIndex].columns[columnIndex].items[itemIndex].id;
                                  next.sections[sectionIndex].columns[columnIndex].items[itemIndex] = replacement;
                                })
                              }
                            >
                              <option value="text">Text</option>
                              <option value="image">Image</option>
                              <option value="button">Button</option>
                            </select>
                            <button
                              className="btn btn-danger"
                              type="button"
                              onClick={() =>
                                mutateLayout((next) => {
                                  next.sections[sectionIndex].columns[columnIndex].items.splice(itemIndex, 1);
                                })
                              }
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {item.type === 'text' && (
                          <div className="homepage-builder__grid">
                            <div className="field" style={{ gridColumn: '1 / -1' }}>
                              <label>Text Content</label>
                              <textarea
                                className="input"
                                rows={4}
                                value={item.content || ''}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].content =
                                      event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Color</label>
                              <input
                                className="input"
                                type="color"
                                value={item.styles?.color || '#111111'}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.color =
                                      event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Font Size</label>
                              <input
                                className="input"
                                type="number"
                                min={10}
                                max={120}
                                value={item.styles?.font_size ?? 18}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.font_size =
                                      Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Font Weight</label>
                              <select
                                className="input"
                                value={item.styles?.font_weight ?? 400}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.font_weight =
                                      Number(event.target.value);
                                  })
                                }
                              >
                                {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((weight) => (
                                  <option key={weight} value={weight}>
                                    {weight}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="field">
                              <label>Font Style</label>
                              <select
                                className="input"
                                value={item.styles?.font_style || 'normal'}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.font_style =
                                      event.target.value;
                                  })
                                }
                              >
                                <option value="normal">Normal</option>
                                <option value="italic">Italic</option>
                              </select>
                            </div>
                            <div className="field">
                              <label>Line Height</label>
                              <input
                                className="input"
                                type="number"
                                min={1}
                                max={2.6}
                                step={0.1}
                                value={item.styles?.line_height ?? 1.5}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.line_height =
                                      Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                          </div>
                        )}

                        {item.type === 'image' && (
                          <div className="homepage-builder__grid">
                            <div className="field" style={{ gridColumn: '1 / -1' }}>
                              <label>Image URL</label>
                              <input
                                className="input"
                                value={item.src || ''}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].src =
                                      event.target.value;
                                  })
                                }
                              />
                              <button
                                className="btn btn-muted"
                                type="button"
                                style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}
                                onClick={() =>
                                  setPickerTarget({
                                    type: 'item-image',
                                    sectionIndex,
                                    columnIndex,
                                    itemIndex,
                                  })
                                }
                              >
                                Choose from Media Library
                              </button>
                            </div>
                            <div className="field">
                              <label>Upload image</label>
                              <input
                                className="input"
                                type="file"
                                accept="image/*"
                                onChange={(event) => {
                                  const file = event.target.files?.[0];
                                  uploadImage(
                                    file,
                                    (url) =>
                                      mutateLayout((next) => {
                                        next.sections[sectionIndex].columns[columnIndex].items[itemIndex].src = url;
                                      }),
                                    `item-${sectionIndex}-${columnIndex}-${itemIndex}`
                                  );
                                  event.target.value = '';
                                }}
                              />
                              {uploadingKey === `item-${sectionIndex}-${columnIndex}-${itemIndex}` && (
                                <small className="text-muted">Uploading...</small>
                              )}
                            </div>
                            <div className="field">
                              <label>Alt Text</label>
                              <input
                                className="input"
                                value={item.alt || ''}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].alt =
                                      event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Image Width %</label>
                              <input
                                className="input"
                                type="number"
                                min={5}
                                max={100}
                                value={item.styles?.width ?? 100}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.width =
                                      Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Object Fit</label>
                              <select
                                className="input"
                                value={item.styles?.fit || 'cover'}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.fit =
                                      event.target.value;
                                  })
                                }
                              >
                                <option value="cover">Cover</option>
                                <option value="contain">Contain</option>
                                <option value="fill">Fill</option>
                              </select>
                            </div>
                            <div className="field">
                              <label>Border Radius (px)</label>
                              <input
                                className="input"
                                type="number"
                                min={0}
                                max={80}
                                value={item.styles?.border_radius ?? 0}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[
                                      itemIndex
                                    ].styles.border_radius = Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                          </div>
                        )}

                        {item.type === 'button' && (
                          <div className="homepage-builder__grid">
                            <div className="field">
                              <label>Button Text</label>
                              <input
                                className="input"
                                value={item.text || ''}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].text =
                                      event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Button URL</label>
                              <input
                                className="input"
                                value={item.url || ''}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].url =
                                      event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Style</label>
                              <select
                                className="input"
                                value={item.styles?.variant || 'solid'}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.variant =
                                      event.target.value;
                                  })
                                }
                              >
                                <option value="solid">Solid</option>
                                <option value="outline">Outline</option>
                              </select>
                            </div>
                            <div className="field">
                              <label>Background</label>
                              <input
                                className="input"
                                type="color"
                                value={item.styles?.background_color || '#1e3a8a'}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[
                                      itemIndex
                                    ].styles.background_color = event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Text Color</label>
                              <input
                                className="input"
                                type="color"
                                value={item.styles?.text_color || '#ffffff'}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.text_color =
                                      event.target.value;
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Font Size</label>
                              <input
                                className="input"
                                type="number"
                                min={10}
                                max={60}
                                value={item.styles?.font_size ?? 16}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.font_size =
                                      Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Font Weight</label>
                              <select
                                className="input"
                                value={item.styles?.font_weight ?? 600}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.font_weight =
                                      Number(event.target.value);
                                  })
                                }
                              >
                                {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((weight) => (
                                  <option key={weight} value={weight}>
                                    {weight}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="field">
                              <label>Border Radius</label>
                              <input
                                className="input"
                                type="number"
                                min={0}
                                max={999}
                                value={item.styles?.border_radius ?? 10}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[
                                      itemIndex
                                    ].styles.border_radius = Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Padding Y</label>
                              <input
                                className="input"
                                type="number"
                                min={4}
                                max={60}
                                value={item.styles?.padding_y ?? 12}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.padding_y =
                                      Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                            <div className="field">
                              <label>Padding X</label>
                              <input
                                className="input"
                                type="number"
                                min={8}
                                max={120}
                                value={item.styles?.padding_x ?? 20}
                                onChange={(event) =>
                                  mutateLayout((next) => {
                                    next.sections[sectionIndex].columns[columnIndex].items[itemIndex].styles.padding_x =
                                      Number(event.target.value);
                                  })
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="homepage-builder__row">
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-muted"
                        type="button"
                        onClick={() =>
                          mutateLayout((next) => {
                            next.sections[sectionIndex].columns[columnIndex].items.push(createTextItem());
                          })
                        }
                      >
                        Add Text
                      </button>
                      <button
                        className="btn btn-muted"
                        type="button"
                        onClick={() =>
                          mutateLayout((next) => {
                            next.sections[sectionIndex].columns[columnIndex].items.push(createImageItem());
                          })
                        }
                      >
                        Add Image
                      </button>
                      <button
                        className="btn btn-muted"
                        type="button"
                        onClick={() =>
                          mutateLayout((next) => {
                            next.sections[sectionIndex].columns[columnIndex].items.push(createButtonItem());
                          })
                        }
                      >
                        Add Button
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <button
                className="btn btn-muted"
                type="button"
                onClick={() =>
                  mutateLayout((next) => {
                    next.sections[sectionIndex].columns.push(createColumn());
                  })
                }
              >
                Add Column
              </button>
            </div>
          </article>
        ))}
      </div>

      <div className="card">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() =>
            mutateLayout((next) => {
              next.sections.push(createSection());
            })
          }
        >
          Add Section
        </button>
      </div>

      <MediaLibraryPickerModal
        open={Boolean(pickerTarget)}
        onClose={() => setPickerTarget(null)}
        onSelect={handleMediaLibrarySelect}
      />
    </section>
  );
};

export default HomepageBuilder;
